import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAppDispatch } from '@/store';
import commonUtils from '@/school-management/utils/common';
import { reportService } from '@/school-management/services/report';
import { adminManagementService } from '@/admin/services/adminManagement';
import { ClassReportContent } from '@/school-management/types/generate-report';
import ClassReport from '@/components/StudentsData/UserData/GenerateReport/ClassReport';
import type { PreFetchedClassReportData } from '@/components/StudentsData/UserData/GenerateReport/ClassReport';
import { generateReportPdfFromElement } from '@/components/StudentsData/UserData/GenerateReport/generateReportPdf';
import { errorActions } from '@/store/reducers/error';

const MOCK_LEVELS = [
  { id: 1, name: 'Level 1' },
  { id: 2, name: 'Level 2' },
  { id: 3, name: 'Level 3' },
  { id: 4, name: 'Level 4' },
  { id: 5, name: 'Level 5' },
];

interface ClassReportProps {
  schoolId: string;
  classId: string;
  className: string;
  classGrade?: string;
  totalStudents?: number;
  onLoadReportError: () => void;
  onReportGenerated?: () => void;
}

const GenerateClassReport = ({
  schoolId,
  classId,
  className,
  classGrade,
  totalStudents,
  onLoadReportError,
  onReportGenerated,
}: ClassReportProps) => {
  const { t, i18n } = useAdminTranslation();
  const dispatch = useAppDispatch();

  const [preFetchedData, setPreFetchedData] = useState<PreFetchedClassReportData | null>(null);
  const reportRef = useRef<HTMLElement | null>(null);
  const [reportReady, setReportReady] = useState(false);
  const hasGeneratedRef = useRef(false);

  useEffect(() => {
    let isActive = true;

    const loadReport = async () => {
      if (!classId || !schoolId) return;

      try {
        const [classReportResponse, dashboardStatsResponse] = await Promise.all([
          reportService.fetchClassReport(classId, schoolId),
          adminManagementService.getUserStatistics({
            groupIds: [classId],
          }),
        ]);

        const responseData = classReportResponse?.data as { data?: ClassReportContent } | undefined;
        const classReport = responseData?.data;

        if (!isActive) return;

        if (!classReport) {
          onLoadReportError();
          return;
        }

        const classroomInfo = {
          name: className || '-',
          grade: classGrade ? commonUtils.getGradeText(classGrade, t) : '-',
          totalStudents: totalStudents?.toString() ?? '-',
        };

        setPreFetchedData({
          classReport: classReport as PreFetchedClassReportData['classReport'],
          dashboardStatistics: dashboardStatsResponse?.data?.data ?? null,
          classroomInfo,
          levels: MOCK_LEVELS,
          interfaceLanguage: i18n.language || 'en',
        });
      } catch (error: any) {
        if (!isActive) return;

        if (error?.statusCode === 404) {
          dispatch(
            errorActions.showError({
              title: t('error.weHadAProblem') || '',
              i18nKey: 'studentDataModal.report.classDataNotAvailable',
            })
          );
        } else {
          onLoadReportError();
        }
      }
    };

    loadReport();

    return () => {
      isActive = false;
    };
  }, [
    classId,
    schoolId,
    className,
    classGrade,
    totalStudents,
    t,
    i18n.language,
    onLoadReportError,
    dispatch,
  ]);

  const setReportRef = useCallback((el: HTMLElement | null) => {
    reportRef.current = el;
    setReportReady(!!el);
  }, []);

  useEffect(() => {
    if (!reportReady || !preFetchedData || hasGeneratedRef.current) return;

    const element = reportRef.current;
    if (!element) return;

    hasGeneratedRef.current = true;
    const filename = `${t('classReport')}.pdf`;

    element.style.position = 'fixed';
    element.style.top = '-9999px';
    element.style.left = '-9999px';
    element.style.visibility = 'visible';
    element.style.display = 'flex';

    generateReportPdfFromElement(element, filename)
      .then(() => {
        onReportGenerated?.();
      })
      .catch(err => {
        console.error('Error generating PDF:', err);
        onLoadReportError();
      })
      .finally(() => {
        hasGeneratedRef.current = false;
        setReportReady(false);
        if (element) {
          element.style.visibility = 'hidden';
          element.style.display = 'none';
        }
      });
  }, [reportReady, preFetchedData, t, onReportGenerated, onLoadReportError]);

  if (!preFetchedData) {
    return null;
  }

  return (
    <ClassReport
      preFetchedData={preFetchedData}
      generateReportPdf={setReportRef}
      onLoadReportError={onLoadReportError}
    />
  );
};

export default GenerateClassReport;
